import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, centavos, esCamion, formatMoney, parseMoney, type Centavos } from '@abastece/shared';
import { dolaresEsperados, efectivoEsperado, responsableDe, type SesionCaja } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { sesionAbierta } from '../lib/caja.js';
import { emitirYVer } from '../lib/emitir.js';
import { atrasHasta, irA } from '../lib/volver.js';
import { bloquear, usePuede, useSesion } from '../lib/sesion.js';
import { Asistente, Respuestas, indicePaso, type Paso } from '../ui/Asistente.js';
import { Campo } from '../ui/Campo.js';
import { enMoneda } from '../ui/ContarBilletes.js';
import { CuentasDelCorte, Dato, type Corte } from '../ui/CuentasDeCaja.js';
import { Ico } from '../ui/Iconos.js';
import {
  GuardadoEnServidor,
  ListaDeAvisos,
  ListaDePedidos,
  ResumenDelDia,
  usePedidosPendientes,
  usePendientesDelDueno,
} from '../ui/LoDelDia.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { LiquidarRuta } from './LiquidarRuta.js';
import { PedirPin } from '../ui/PedirPin.js';
import { SIN_CONTAR, contado, pasoDeConteo, primerConteo, type Contando } from '../ui/PasoDeConteo.js';
import { useT } from '../i18n/index.js';

/**
 * «Terminar mi turno» y «Terminar el día»: el corte de caja, un paso a la vez.
 *
 * The till is counted at the end of every shift (the owner, 2026-10-03), and whether anybody comes
 * after cannot be known from here, so it is the first thing asked. Then the drawer, note by note,
 * córdobas and dollars, each blind and asked again once when it is off by `cajaReconteoDesde` or
 * more; what stays for change, for the next person or for tomorrow; and who takes the rest. The
 * corte is made on the last question, and what it found is said after it.
 *
 * When the shop is closing, the end of the shift is the end of the day too, and four more steps
 * follow: the day's summary to print or send to the owner, what the owner has left to see to,
 * tomorrow's orders, and whether everything has reached the server before the phone goes off. A
 * shift with somebody after it ends handing the device over: the PIN pad, for the next person to
 * sign in and start their own, counting what this one left.
 */
export function TerminarTurno() {
  const t = useT();
  const sesion = useSesion();
  const puedeRecibir = usePuede('recibir_efectivo');
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The till open on this device: null for none, undefined while it is read. */
  const abiertaAqui = useLiveQuery(async () => (await sesionAbierta()) ?? null, [], undefined);
  /**
   * The route day whose till this is, if it is a route's: one left open from before «Reparto» was
   * taken out (2026-10-04). That till closes with the route's settlement, which counts the goods
   * and the cash together (`LiquidarRuta`), and a corte would be refused. Said with the till it
   * was read for: until it is this one's, the corte's first step would flash up over a route's.
   */
  const ruta = useLiveQuery(
    async () => ({
      de: abiertaAqui?.id ?? null,
      id:
        abiertaAqui && esCamion(abiertaAqui.ubicacion)
          ? ((await db.jornadas.where('estado').equals('abierta').filter((j) => j.sesionId === abiertaAqui.id).first())?.id ?? null)
          : null,
    }),
    [abiertaAqui?.id, abiertaAqui?.ubicacion],
  );
  const rutaLeida = ruta !== undefined && ruta.de === (abiertaAqui?.id ?? null);
  /**
   * Kept once seen: settled, the till is gone from the query a moment before what the settlement
   * found is drawn, and the screen would have said there was no till.
   */
  const [rutaVista, setRutaVista] = useState<string | null>(null);
  const rutaAqui = rutaLeida ? ruta.id : null;
  useEffect(() => {
    if (rutaAqui) setRutaVista(rutaAqui);
  }, [rutaAqui]);
  const deRuta = rutaAqui ?? rutaVista;
  /** Whose the drawer is, when it is not the person ending it. */
  const deQuien = useLiveQuery(
    async () =>
      abiertaAqui && sesion && responsableDe(abiertaAqui) !== sesion.usuarioId
        ? ((await db.usuarios.get(responsableDe(abiertaAqui)))?.nombre ?? null)
        : null,
    [abiertaAqui?.id, sesion?.usuarioId],
    null,
  );

  const [actual, setActual] = useState('quien');
  /** Whether the shop closes after this shift: unanswered, or the answer. */
  const [dia, setDia] = useState<boolean | null>(null);
  const [cordobas, setCordobas] = useState<Contando>(SIN_CONTAR);
  const [dolares, setDolares] = useState<Contando>(SIN_CONTAR);
  /** What was typed to leave; null until then, while the suggestion stands. */
  const [quedaEscrita, setQuedaEscrita] = useState<string | null>(null);
  const [notas, setNotas] = useState('');
  /** For whoever may not receive cash: whether the owner takes it now, with their PIN, or later. */
  const [recibeAhora, setRecibeAhora] = useState<boolean | null>(null);
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The corte made, and the till it closed: from here on the steps say, and ask nothing. */
  const [cerrada, setCerrada] = useState<{ corte: Corte; caja: SesionCaja } | null>(null);
  /**
   * The till the corte is being made of, from the tap: once the corte is saved the open till is
   * gone from the query a moment before the result is drawn, and the screen said there was none.
   */
  const [deLaCorte, setDeLaCorte] = useState<SesionCaja | null>(null);
  /** Where the step on screen was, for when it goes (`indicePaso`). */
  const ultimo = useRef(0);

  const pendientes = usePendientesDelDueno();
  const manana = usePedidosPendientes('manana');

  const caja = cerrada?.caja ?? deLaCorte ?? abiertaAqui ?? null;
  const titulo = t(dia ? 'turno.terminarDia' : 'turno.terminarTurno');

  if (deRuta) return <LiquidarRuta jornadaId={deRuta} />;
  // Over an open till, until it is known whether it is a route's; never over a corte just made.
  if (abiertaAqui === undefined || (abiertaAqui && !rutaLeida)) return <Pagina titulo={titulo}>{null}</Pagina>;
  if (!caja) {
    return (
      <Pagina titulo={titulo} volverA="/caja">
        <Vacio mensaje={t('turno.noHayCaja')} icono="caja">
          <Link to="/caja/empezar" className="btn-primario px-5">
            <Ico icono="caja" />
            {t('turno.empezarDia')}
          </Link>
        </Vacio>
      </Pagina>
    );
  }
  const esperadoC = efectivoEsperado(caja);
  const esperadoD = dolaresEsperados(caja);
  const cC = contado(cordobas, 'NIO');
  const cD = contado(dolares, 'USD');
  /**
   * What the corte suggests leaving: the shop's float, set in «Configuración», or nothing (the
   * owner, 2026-10-14: «the default number for "how much do you leave in the drawer for change"
   * should be ZERO in the closing process»; it was what the shift opened with); never more than
   * counted. A truck's leaves nothing, as it always has: no till opens at a truck any more
   * («Reparto» is gone), and one opened there before, whose route is not open, is emptied rather
   * than leave a float in a drawer nobody opens again.
   */
  const deCamion = esCamion(caja.ubicacion);
  const quedaSugerida = deCamion ? (0 as Centavos) : centavos(config.cajaFondo ?? 0);
  const sugerida = cC !== null && cC < quedaSugerida ? cC : quedaSugerida;
  const queda = quedaEscrita ?? formatMoney(sugerida, { symbol: false });
  const qC = parseMoney(queda);
  const quedaMalEscrita = queda.trim() === '' || qC === null || qC < 0;
  const quedaDeMas = cC !== null && qC !== null && qC > cC;
  const sale = cC !== null && qC !== null && !quedaDeMas ? ((cC - qC) as Centavos) : null;

  async function cerrar(recibidoPor?: string) {
    if (!caja || cC === null || cD === null || qC === null || quedaMalEscrita || quedaDeMas) return;
    setCerrando(true);
    setDeLaCorte(caja);
    setError(null);
    const [primeroC, primeroD] = [primerConteo(cordobas, cC), primerConteo(dolares, cD)];
    try {
      const { rechazo } = await emitirYVer('caja.cerrada', {
        sesionId: caja.id,
        conteoCordobas: cC,
        conteoDolares: cD,
        // Dollars leave the drawer at the official rate, which is what they are carried at.
        tasaCambio: config.fxTasaOficial,
        queda: qC,
        ...(notas.trim() ? { notas: notas.trim() } : {}),
        ...(recibidoPor ? { recibidoPor } : {}),
        ...(primeroC !== null ? { primerConteoCordobas: primeroC } : {}),
        ...(primeroD !== null ? { primerConteoDolares: primeroD } : {}),
      });
      // Refused (closed meanwhile on another screen, say): said here, and nothing claims a corte.
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setCerrada({
        caja,
        corte: {
          esperadoC,
          contadoC: cC,
          esperadoD,
          contadoD: cD,
          saque: { queda: qC, cordobas: (cC - qC) as Centavos, dolares: cD, recibido: Boolean(recibidoPor) },
          primerConteoC: primeroC,
          primerConteoD: primeroD,
        },
      });
      setActual('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setCerrando(false);
    }
  }

  function terminar() {
    // Whoever holds the safe takes it in hand themselves; anybody else, on the owner's PIN or later.
    if (puedeRecibir) void cerrar(sesion?.usuarioId);
    else if (recibeAhora) setPidiendoPin(true);
    else void cerrar();
  }

  const paraManana = dia ? 'turno.paraManana' : 'turno.paraElSiguiente';

  // --- Before the corte: each step can be gone back to. ---
  const antes: Paso[] = [
    {
      id: 'quien',
      titulo: t('turno.quienSigue'),
      explicacion: <p>{t('turno.quienSigueExplicacion')}</p>,
      contenido: (
        <Respuestas
          opciones={[
            {
              etiqueta: t('turno.sigueOtra'),
              elegida: dia === false,
              detalle: t('turno.sigueOtraDetalle'),
              icono: 'personal',
              alTocar: () => {
                setDia(false);
                siguiente();
              },
            },
            {
              etiqueta: t('turno.seCierra'),
              elegida: dia === true,
              detalle: t('turno.seCierraDetalle'),
              icono: 'bloquear',
              alTocar: () => {
                setDia(true);
                siguiente();
              },
            },
          ]}
        />
      ),
    },
    pasoDeConteo({
      id: 'cordobas',
      moneda: 'NIO',
      contando: cordobas,
      poner: setCordobas,
      esperado: esperadoC,
      umbral: config.cajaReconteoDesde,
      titulo: t('turno.contarCordobas'),
      explicacion: config.cajaConteoCiego ? t('turno.contarCiego') : t('turno.contarCordobasExplicacion'),
      guia: 'turno-cordobas',
      // An empty drawer in a tap, as the dollars have it (the owner, 2026-10-14).
      sinCordobas: true,
      siguiente: () => siguiente(),
      t,
    }),
    pasoDeConteo({
      id: 'dolares',
      moneda: 'USD',
      contando: dolares,
      poner: setDolares,
      esperado: esperadoD,
      umbral: config.cajaReconteoDesde,
      titulo: t('turno.contarDolares'),
      explicacion: t('turno.contarDolaresCerrar'),
      siguiente: () => siguiente(),
      t,
    }),
    // What stays for change. Not asked on a truck, whose drawer the corte empties (`deCamion`).
    ...(deCamion
      ? []
      : [
          {
            id: 'queda',
            titulo: t('turno.quedaTitulo'),
            guia: 'turno-queda',
            explicacion: <p>{t(quedaSugerida > 0 ? 'turno.quedaExplicacion' : 'turno.quedaExplicacionCero', { para: t(paraManana) })}</p>,
            contenido: (
              <div className="flex flex-col gap-4">
                <Campo
                  etiqueta={t('turno.quedaCampo')}
                  inputMode="decimal"
                  className="tabular text-lg"
                  value={queda}
                  onChange={(e) => setQuedaEscrita(e.target.value)}
                  error={
                    queda.trim() !== '' && (qC === null || qC < 0)
                      ? t('caja.montoInvalido')
                      : quedaDeMas
                        ? t('caja.quedaMasQueContado', { monto: formatMoney(cC!) })
                        : null
                  }
                  ayuda={t('turno.quedaAyuda')}
                />
                {sale !== null && (
                  <div className="tarjeta flex flex-col gap-2">
                    <Dato etiqueta={t('turno.saleCordobas')} valor={formatMoney(sale)} />
                    {(cD ?? 0) > 0 && <Dato etiqueta={t('turno.saleDolares')} valor={enMoneda(cD!, 'USD')} />}
                  </div>
                )}
              </div>
            ),
            seguir: {
              etiqueta: t('asistente.siguiente'),
              deshabilitado: quedaMalEscrita || quedaDeMas,
              alTocar: () => siguiente(),
            },
          } satisfies Paso,
        ]),
    {
      id: 'entrega',
      titulo: t('turno.quienRecibe'),
      explicacion: <p>{t(puedeRecibir ? 'caja.ustedRecibe' : 'turno.quienRecibeExplicacion')}</p>,
      ...(puedeRecibir
        ? { seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() } }
        : {
            contenido: (
              <Respuestas
                opciones={[
                  {
                    etiqueta: t('turno.recibeAhora'),
                    elegida: recibeAhora === true,
                    detalle: t('caja.recibeAhoraAyuda'),
                    icono: 'recibir',
                    alTocar: () => {
                      setRecibeAhora(true);
                      siguiente();
                    },
                  },
                  {
                    etiqueta: t('turno.recibeDespues'),
                    elegida: recibeAhora === false,
                    detalle: t('caja.porRecibirAyuda'),
                    icono: 'despues',
                    alTocar: () => {
                      setRecibeAhora(false);
                      siguiente();
                    },
                  },
                ]}
              />
            ),
          }),
    },
    {
      id: 'confirmar',
      titulo: t('turno.revise'),
      explicacion: <p>{t('turno.reviseCerrar')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            <Dato etiqueta={t('turno.contoCordobas')} valor={formatMoney(cC ?? (0 as Centavos))} />
            <Dato etiqueta={t('turno.contoDolares')} valor={enMoneda(cD ?? (0 as Centavos), 'USD')} />
            <Dato etiqueta={t('turno.quedaCampo')} valor={formatMoney(qC ?? (0 as Centavos))} />
            {sale !== null && <Dato etiqueta={t('turno.saleCordobas')} valor={formatMoney(sale)} />}
            {(cD ?? 0) > 0 && <Dato etiqueta={t('turno.saleDolares')} valor={enMoneda(cD!, 'USD')} />}
            <Dato
              etiqueta={t('turno.loRecibe')}
              valor={t(puedeRecibir ? 'turno.recibeUsted' : recibeAhora ? 'turno.recibeElDueno' : 'turno.quedaPorRecibir')}
            />
          </div>
          <Campo
            maxLength={500}
            etiqueta={t('turno.notas')}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            autoComplete="off"
            ayuda={t('turno.notasAyuda')}
          />
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: {
        etiqueta: titulo,
        icono: 'corte',
        deshabilitado: cerrando || cC === null || cD === null || quedaMalEscrita || quedaDeMas,
        alTocar: terminar,
      },
    },
  ];

  // --- Once the corte is made: what it found, and, when the shop is closing, the day. ---
  const despues: Paso[] = [
    {
      id: 'resultado',
      titulo: t('turno.resultadoTitulo'),
      sinAtras: true,
      // The drawer as it stood when it was counted: what each expected figure was made of.
      contenido: cerrada ? <CuentasDelCorte corte={cerrada.corte} caja={cerrada.caja} /> : null,
      seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
    },
    ...(dia
      ? [
          {
            id: 'resumen',
            titulo: t('turno.resumenTitulo'),
            sinAtras: true,
            explicacion: <p>{t('turno.resumenExplicacion')}</p>,
            contenido: <ResumenDelDia dispositivoId={caja.dispositivoId} ubicacion={caja.ubicacion} />,
            seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
          } satisfies Paso,
          ...((pendientes?.length ?? 0) > 0
            ? [
                {
                  id: 'pendientes',
                  titulo: t('turno.pendientesTitulo'),
                  sinAtras: true,
                  explicacion: <p>{t('turno.pendientesExplicacion')}</p>,
                  contenido: <ListaDeAvisos avisos={pendientes!} />,
                  seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
                } satisfies Paso,
              ]
            : []),
          ...((manana?.length ?? 0) > 0
            ? [
                {
                  id: 'manana',
                  titulo: t('turno.mananaTitulo'),
                  sinAtras: true,
                  explicacion: <p>{t('turno.mananaExplicacion')}</p>,
                  contenido: <ListaDePedidos pedidos={manana!} />,
                  seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
                } satisfies Paso,
              ]
            : []),
          {
            id: 'guardado',
            titulo: t('turno.guardadoTitulo'),
            sinAtras: true,
            explicacion: <p>{t('turno.guardadoExplicacion')}</p>,
            contenido: <GuardadoEnServidor />,
            seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
          } satisfies Paso,
        ]
      : []),
    dia
      ? {
          id: 'listo',
          titulo: t('turno.hastaManana'),
          guia: 'turno-listo',
          sinAtras: true,
          explicacion: <p>{t('turno.hastaMananaExplicacion')}</p>,
          seguir: { etiqueta: t('turno.irAlInicio'), icono: 'inicio', alTocar: () => void irA({ to: '/' }) },
        }
      : {
          id: 'listo',
          titulo: t('turno.turnoTerminado'),
          guia: 'turno-listo',
          sinAtras: true,
          explicacion: <p>{t('turno.turnoTerminadoExplicacion')}</p>,
          contenido: (
            <Link to="/" replace onClick={atrasHasta('/')} className="btn-secundario">
              <Ico icono="inicio" />
              {t('turno.irAlInicio')}
            </Link>
          ),
          seguir: {
            etiqueta: t('turno.pasarALaSiguiente'),
            icono: 'bloquear',
            // Home first, so whoever signs in next lands where «Empezar mi turno» is: back to it when
            // the corte was opened from there, leaving nothing of the shift to come back through.
            alTocar: () => void irA({ to: '/' }).then(() => bloquear()),
          },
        },
  ];

  const pasos = cerrada ? despues : antes;
  const ids = pasos.map((p) => p.id);
  const i = indicePaso(ids, actual, ultimo.current);
  ultimo.current = i;
  const paso = pasos[i]!;
  function siguiente() {
    setActual(ids[i + 1] ?? ids[i]!);
  }
  const todos = [...antes, ...despues].map((p) => p.id);

  return (
    <>
      <Asistente
        titulo={titulo}
        subtitulo={deQuien ? t('turno.cajaDe', { nombre: deQuien }) : t('turno.subtitulo')}
        paso={paso}
        numero={todos.indexOf(paso.id) + 1}
        total={todos.length}
        volverA="/caja"
        {...(i > 0 ? { onAtras: () => setActual(ids[i - 1]!) } : {})}
      />
      {pidiendoPin && (
        <PedirPin
          motivo={t('caja.motivoRecibir')}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={(duenoId) => {
            setPidiendoPin(false);
            void cerrar(duenoId);
          }}
        />
      )}
    </>
  );
}
